// Putting bytes in the library: a resumable session, or a small file in one request.
//
// A session's chunks do not go through `api`: they go to the `location` it answers, on the
// `visitor` surface, carrying the session's own token rather than the session cookie.

import { api } from '../client'
import type { components } from '../schema'

export type OpenAssetUploadRequest = components['schemas']['OpenAssetUploadRequest']
export type AssetUploadOpened = components['schemas']['AssetUploadOpened']
export type AssetUploaded = components['schemas']['AssetUploaded']
export type AssetUploadMode = components['schemas']['AssetUploadMode']
export type RelationshipKind = components['schemas']['RelationshipKind']

/** The header a chunk request carries its session's `upload_token` in: that session's whole credential. */
export const UPLOAD_TOKEN_HEADER = 'X-Upload-Token'

/**
 * Open a resumable upload. The asset exists from this answer on, `uploading`, and carries no file
 * until the last chunk lands at `location`. `create` names a workspace and optionally a folder;
 * `version` and `related` name the target asset, whose workspace they act in, and nothing else —
 * a field the mode does not take is a 400.
 */
export function openAssetUpload(request: OpenAssetUploadRequest): Promise<AssetUploadOpened> {
  return api.post<AssetUploadOpened>('/assets/uploads', request)
}

/**
 * Put a small file in the library in one request: when this answers, the asset exists and carries
 * its file. The server buffers the body and refuses one past 100 MB with a 413 — a larger file
 * opens a session instead.
 */
export function uploadAssetDirect(
  request: OpenAssetUploadRequest,
  file: File,
  signal?: AbortSignal,
): Promise<AssetUploaded> {
  const form = new FormData()
  form.append('metadata', JSON.stringify(request))
  form.append('file', file)
  return api.postForm<AssetUploaded>('/assets/uploads/direct', form, signal)
}
